import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { CalendarIcon, EyeIcon, XIcon, AlertCircleIcon, ChevronDownIcon, CheckCircleIcon, AlertTriangleIcon, CreditCardIcon, WalletIcon, RepeatIcon, TrendingUpIcon, CheckIcon } from 'lucide-react';
import { mockBills, scheduleBills } from '../mocks/mockBills';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
import { getFinancingOffers } from '../mocks/financingData';
import { BRAND } from '../../../shared/config/brand';
type ScheduleType = 'due_date' | 'today' | 'next_cycle';
interface BillRowState {
  billId: string;
  paymentMethod: string;
  designatedApprover: string;
  hasError: boolean;
}
const APPROVERS = [{
  value: 'auto',
  label: 'Assign Automatically'
}, {
  value: 'Simon Verraest',
  label: 'Simon Verraest'
}, {
  value: 'John Smith',
  label: 'John Smith'
}, {
  value: 'Sarah Johnson',
  label: 'Sarah Johnson'
}];
function getSupplierPaymentMethod(supplierId: string) {
  const supplier = mockSuppliers.find(s => s.id === supplierId);
  if (!supplier) return {
    method: '',
    label: 'No payment method',
    options: []
  };
  if (supplier.payInMethod === 'card' && supplier.cardNumber) {
    const last4 = supplier.cardNumber.slice(-4);
    const brand = supplier.cardNumber.startsWith('4') ? 'Visa' : 'Mastercard';
    return {
      method: 'card',
      label: `${brand} ending in ${last4}`,
      options: [{
        value: `card_${supplierId}`,
        label: `${brand} ending in ${last4}`
      }, {
        value: 'acquiring',
        label: 'Acquiring Balance'
      }]
    };
  }
  return {
    method: 'acquiring',
    label: 'Acquiring Balance',
    options: [{
      value: 'acquiring',
      label: 'Acquiring Balance'
    }]
  };
}
function getNextCycleDate(cycleDays: number[]): {
  date: string;
  label: string;
} | null {
  if (cycleDays.length === 0) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentDay = today.getDate();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  let nextDate: Date | null = null;
  for (const day of cycleDays) {
    let candidate: Date;
    if (day === -1) {
      // Last day of current month
      const lastDayThisMonth = new Date(currentYear, currentMonth + 1, 0);
      if (lastDayThisMonth > today) {
        candidate = lastDayThisMonth;
      } else {
        // Last day of next month
        candidate = new Date(currentYear, currentMonth + 2, 0);
      }
    } else {
      // Specific day of month
      candidate = new Date(currentYear, currentMonth, day);
      if (candidate <= today) {
        // Move to next month
        candidate = new Date(currentYear, currentMonth + 1, day);
      }
    }
    if (!nextDate || candidate < nextDate) {
      nextDate = candidate;
    }
  }
  if (!nextDate) return null;
  return {
    date: nextDate.toISOString().split('T')[0],
    label: nextDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  };
}
function getPaymentDate(scheduleType: ScheduleType, dueDate: string, cycleDays?: number[]): string {
  if (scheduleType === 'today') {
    return new Date().toISOString().split('T')[0];
  }
  if (scheduleType === 'next_cycle' && cycleDays) {
    const nextCycle = getNextCycleDate(cycleDays);
    if (nextCycle) return nextCycle.date;
    // Fallback to today if no cycles configured
    return new Date().toISOString().split('T')[0];
  }
  // due_date: use the bill's due date, but if it's in the past, use today
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due < today) {
    return today.toISOString().split('T')[0];
  }
  return dueDate;
}
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}
export function BillApprovalWizard() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get('ids');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('due_date');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [fundingMode, setFundingMode] = useState<'standard' | 'financing'>('standard');
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  // Read payment cycles from localStorage
  const paymentCycles = useMemo(() => {
    const stored = localStorage.getItem('billPaymentCycles');
    return stored ? JSON.parse(stored) as number[] : [];
  }, []);
  const nextCycleInfo = useMemo(() => getNextCycleDate(paymentCycles), [paymentCycles]);
  // Get selected bills
  const selectedBills = useMemo(() => {
    if (id && id !== 'schedule') {
      return mockBills.filter(b => b.id === id);
    }
    if (idsParam) {
      const ids = idsParam.split(',');
      return mockBills.filter(b => ids.includes(b.id));
    }
    return [];
  }, [id, idsParam]);
  // Per-bill row state
  const [billRows, setBillRows] = useState<BillRowState[]>([]);
  // Initialize bill row state from supplier data
  useEffect(() => {
    if (selectedBills.length > 0 && billRows.length === 0) {
      const rows = selectedBills.map(bill => {
        const supplierPay = getSupplierPaymentMethod(bill.supplierId);
        const defaultMethod = supplierPay.options.length > 0 ? supplierPay.options[0].value : '';
        return {
          billId: bill.id,
          paymentMethod: defaultMethod,
          designatedApprover: 'auto',
          hasError: !defaultMethod
        };
      });
      setBillRows(rows);
    }
  }, [selectedBills]);
  const totalAmount = selectedBills.reduce((sum, b) => sum + b.amount, 0);
  const financingOffers = useMemo(() => getFinancingOffers(totalAmount), [totalAmount]);
  // Validation
  const allValid = fundingMode === 'financing' ? !!selectedOfferId : billRows.every(r => r.paymentMethod !== '');
  const invalidCount = billRows.filter(r => r.paymentMethod === '').length;
  // Redirect if no bills selected
  useEffect(() => {
    if (selectedBills.length === 0) {
      navigate('/bills');
    }
  }, [selectedBills, navigate]);
  const updateBillRow = (billId: string, updates: Partial<BillRowState>) => {
    setBillRows(prev => prev.map(r => {
      if (r.billId !== billId) return r;
      const updated = {
        ...r,
        ...updates
      };
      updated.hasError = !updated.paymentMethod;
      return updated;
    }));
  };
  const handleConfirmSchedule = () => {
    if (!allValid) {
      setShowValidationErrors(true);
      return;
    }
    const assignments: Record<string, {
      approver: string;
      paymentMethod: string;
      paymentDate: string;
    }> = {};
    billRows.forEach(row => {
      const bill = selectedBills.find(b => b.id === row.billId);
      if (bill) {
        assignments[row.billId] = {
          approver: row.designatedApprover,
          paymentMethod: fundingMode === 'financing' ? `financing_${selectedOfferId}` : row.paymentMethod,
          paymentDate: getPaymentDate(scheduleType, bill.dueDate, paymentCycles)
        };
      }
    });
    scheduleBills(billRows.map(r => r.billId), assignments);
    setStep(3);
  };
  // Compute which bills are assigned to "me" (Simon Verraest)
  const assignedToMe = billRows.filter(r => r.designatedApprover === 'Simon Verraest');
  const autoAssigned = billRows.filter(r => r.designatedApprover === 'auto');
  if (selectedBills.length === 0) return null;
  return <div className="fixed inset-0 z-50 bg-[#F8F9FA] overflow-y-auto" data-id="element-3582" data-mp="26xtxbwq1ykly">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-3583" data-mp="2273qsg5w4bpo">
        <button onClick={() => navigate('/bills')} aria-label="Close wizard" className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors" data-id="element-3584" data-mp="1y6bjvtkhqiwk">
          <XIcon size={20} aria-hidden="true" data-id="element-3585" data-mp="1o6b0k7mmg2sd" />
        </button>

        {/* Progress Bar */}
        <div className="flex items-center gap-3" data-id="element-3586" data-mp="1j9ap1h7067tv">
          <span className="text-xs text-gray-500 font-medium" data-id="element-3587" data-mp="1ja2l0t4ii7ko">
            {t('pages.BillApprovalWizard.step')} {step}{' '}
            {t('pages.BillApprovalWizard.of_3')}
          </span>

          <div className="w-64 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-3588" data-mp="3dk67rcbwj3ad">
            <div className="h-full bg-gradient-to-r from-[#E2231A] to-[#EB4A42] transition-all duration-300 ease-in-out rounded-full" style={{
            width: step === 1 ? '33%' : step === 2 ? '66%' : '100%'
          }} data-id="element-3589" data-mp="39lgoj346mrit" />
          </div>
        </div>
        <div className="w-10" data-id="element-3590" data-mp="153itpb4d5lvl" />
      </div>

      <div className="max-w-6xl mx-auto py-10 px-4 sm:px-6" data-id="element-3591" data-mp="1942ow95dmff3">
        {/* ========== STEP 1: Schedule Selection ========== */}
        {step === 1 && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-3xl mx-auto" data-id="element-3592" data-mp="cfwet6poqutf">
            <div className="text-center mb-10" data-id="element-3593" data-mp="hdern86tpu6p">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-6" data-id="element-3594" data-mp="le8fk1k9n8zl">
                <CalendarIcon size={24} className="text-gray-900" data-id="element-3595" data-mp="qf4hwaoyyn2w" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-3596" data-mp="1wtdk7b3yjhlu">
                {t('pages.BillApprovalWizard.when_would_you_like_the_payments')}
              </h1>
              <p className="text-sm text-gray-500" data-id="element-3597" data-mp="2rpoi8kegjf0b">
                {t('pages.BillApprovalWizard.select_one_of_the_options_below')}
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-3598" data-mp="31epxu9h3budr">
              <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'due_date' ? 'border-[#E2231A] ring-1 ring-[#E2231A] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3599" data-mp="31g11m9ajd24v">
                <div className="flex items-start" data-id="element-3600" data-mp="2dovlru4li9uk">
                  <div className="flex items-center h-5" data-id="element-3601" data-mp="2dpnhd4y707ku">
                    <input type="radio" name="scheduleType" value="due_date" checked={scheduleType === 'due_date'} onChange={() => setScheduleType('due_date')} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-3602" data-mp="2inp0fpvmc6wb" />
                  </div>
                  <div className="ml-4" data-id="element-3603" data-mp="1mgkach0aizjn">
                    <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-3604" data-mp="1mjpz1v50bzeu">
                      {t('pages.BillApprovalWizard.payment_scheduled_on_invoice_due_date')}
                    </span>
                    <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-3605" data-mp="18p5rqbaap0d4">
                      {t('pages.BillApprovalWizard.the_payments_for_the_selected_bills')}
                    </span>
                  </div>
                </div>
              </label>

              <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'today' ? 'border-[#E2231A] ring-1 ring-[#E2231A] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3606" data-mp="ur8tlag70lwc">
                <div className="flex items-start" data-id="element-3607" data-mp="2k5nefjo3bomk">
                  <div className="flex items-center h-5" data-id="element-3608" data-mp="2k5x7xh8cgdx0">
                    <input type="radio" name="scheduleType" value="today" checked={scheduleType === 'today'} onChange={() => setScheduleType('today')} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-3609" data-mp="339h6hdh2w8es" />
                  </div>
                  <div className="ml-4" data-id="element-3610" data-mp="3mxq7md0lnoqf">
                    <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-3611" data-mp="3myrlep902pbu">
                      {t('pages.BillApprovalWizard.payment_scheduled_today')}
                    </span>
                    <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-3612" data-mp="3czk5pvesmilk">
                      {t('pages.BillApprovalWizard.the_payments_for_the_selected_bills_2')}
                    </span>
                  </div>
                </div>
              </label>

              {paymentCycles.length > 0 && <label className={`block bg-white border rounded-xl p-6 cursor-pointer transition-all ${scheduleType === 'next_cycle' ? 'border-[#E2231A] ring-1 ring-[#E2231A] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3613" data-mp="1yzfn94c8fdwe">
                  <div className="flex items-start" data-id="element-3614" data-mp="231sqnm3iazq0">
                    <div className="flex items-center h-5" data-id="element-3615" data-mp="232c00ef3cfsf">
                      <input type="radio" name="scheduleType" value="next_cycle" checked={scheduleType === 'next_cycle'} onChange={() => setScheduleType('next_cycle')} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-3616" data-mp="1fbq2hh2pqdwo" />
                    </div>
                    <div className="ml-4" data-id="element-3617" data-mp="wk5phnf2ce4d">
                      <span className="block text-sm font-semibold text-gray-900 mb-1" data-id="element-3618" data-mp="rmnfrut81uvb">
                        {t('pages.BillApprovalWizard.payment_scheduled_on_next_cycle')}
                      </span>
                      <span className="block text-xs text-gray-500 leading-relaxed" data-id="element-3619" data-mp="1a8z2npo11gu5">
                        {t('pages.BillApprovalWizard.the_payments_for_the_selected_bills_3')}
                      </span>
                      {nextCycleInfo && <div className="mt-2 p-2 bg-[#F0F2F5] rounded text-xs text-gray-600" data-id="element-3620" data-mp="1nzm07ei1x0on">
                          {t('pages.BillApprovalWizard.next_cycle')}{' '}
                          {nextCycleInfo.label}
                        </div>}
                    </div>
                  </div>
                </label>}
            </div>

            <div className="text-center mb-10" data-id="element-3621" data-mp="3l21ywmwc1do">
              <p className="text-xs text-gray-500" data-id="element-3622" data-mp="3md5cvcfj749">
                {t('pages.BillApprovalWizard.define_your_payment_cycle_to_align')}{' '}
                <button className="text-[#9F140F] font-medium hover:underline" data-id="element-3623" data-mp="2luoagwcm43jz">
                  {t('pages.BillApprovalWizard.go_to_bills_settings')}
                </button>
              </p>
            </div>

            <div className="flex justify-center gap-4" data-id="element-3624" data-mp="2cyypgof2tb74">
              <button onClick={() => navigate('/bills')} className="px-8 py-2.5 text-sm font-medium text-[#9F140F] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3625" data-mp="2831bupv76iy1">
                {t('pages.BillApprovalWizard.cancel')}
              </button>
              <button onClick={() => setStep(2)} className="px-8 py-2.5 text-sm font-medium text-white bg-[#E2231A] rounded-lg hover:bg-[#C41B14] transition-colors shadow-sm" data-id="element-3626" data-mp="2vpq15wmjddoo">
                {t('pages.BillApprovalWizard.next')}
              </button>
            </div>
          </div>}

        {/* ========== STEP 2: Review & Confirm ========== */}
        {step === 2 && <div className="animate-in fade-in slide-in-from-right-8 duration-300" data-id="element-3627" data-mp="1ltb27glsm0do">
            <div className="text-center mb-8" data-id="element-3628" data-mp="1lr6z30vpx5eg">
              <div className="w-12 h-12 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-6" data-id="element-3629" data-mp="17sqjsljsi6kp">
                <EyeIcon size={24} className="text-gray-900" data-id="element-3630" data-mp="1cuzbzfou94lr" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-3631" data-mp="y2nryooemyny">
                {t('pages.BillApprovalWizard.review_and_confirm')}
              </h1>
              <p className="text-sm text-gray-500" data-id="element-3632" data-mp="p43eq31m5h74">
                {t('pages.BillApprovalWizard.please_review_the_details_carefully_before')}
              </p>
            </div>

            {/* Info Banner */}
            <div className="bg-[#F0F2F5] rounded-xl p-5 mb-6 flex items-start" data-id="element-3633" data-mp="f77gcq5fibga">
              <AlertCircleIcon size={20} className="text-gray-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-3634" data-mp="k3o4hkcmoptd" />

              <div data-id="element-3635" data-mp="3roqf0ztxrbf3">
                <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-3636" data-mp="3rp8gcavggftv">
                  {t('pages.BillApprovalWizard.how_to_approve_bill_payment_schedules')}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed" data-id="element-3637" data-mp="5gpfdw18u7ht">
                  {t('pages.BillApprovalWizard.after_submitting_the_bill_payment_schedules')}
                </p>
              </div>
            </div>

            {/* Summary Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-3638" data-mp="t61o6usnsdjl">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50" data-id="element-3639" data-mp="o9b4lxhnaztf">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-3640" data-mp="126zu5imnqv4t">
                  {t('pages.BillApprovalWizard.summary')}
                </h3>
              </div>
              <div className="p-6" data-id="element-3641" data-mp="24l4438fk0c3v">
                <div className="grid grid-cols-3 gap-6" data-id="element-3642" data-mp="24k2i48x0257d">
                  <div data-id="element-3643" data-mp="29k8e21aaydv9">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-3644" data-mp="29jp3j5mzerr9">
                      {t('pages.BillApprovalWizard.total_payments')}
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-3645" data-mp="2ehgzcaqqyy0q">
                      {selectedBills.length}
                    </span>
                  </div>
                  <div data-id="element-3646" data-mp="1lw772wsrv5hy">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-3647" data-mp="1lst87yjbhef9">
                      {t('pages.BillApprovalWizard.total_amount')}
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-3648" data-mp="1utrdek5hrrzf">
                      {t('pages.BillApprovalWizard.usd')}{' '}
                      {totalAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                    </span>
                  </div>
                  <div data-id="element-3649" data-mp="173n8m9gkhm6u">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-3650" data-mp="zlyw4chuliit">
                      {t('pages.BillApprovalWizard.payment_schedule')}
                    </span>
                    <span className="text-lg font-semibold text-gray-900" data-id="element-3651" data-mp="ul1crwl3qifq">
                      {scheduleType === 'due_date' ? 'On due date' : scheduleType === 'today' ? 'Immediately' : 'Next cycle'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Funding Mode Toggle */}
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-3652" data-mp="6wrz5hy1l0zo">
              <button onClick={() => setFundingMode('standard')} className={`p-4 rounded-xl border text-left transition-all ${fundingMode === 'standard' ? 'border-[var(--brand-primary)] ring-1 ring-[var(--brand-primary)] bg-white shadow-sm' : 'border-gray-200 bg-gray-50 hover:bg-white'}`} data-id="element-3653" data-mp="6v713iciqzx2">
                <div className="flex items-center gap-3 mb-1" data-id="element-3654" data-mp="3nv37c1klq0ru">
                  <WalletIcon size={18} className={fundingMode === 'standard' ? 'text-[var(--brand-primary)]' : 'text-gray-500'} data-id="element-3655" data-mp="3nsgw9ryi534v" />
                  <span className={`font-semibold ${fundingMode === 'standard' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-3656" data-mp="38zn7ffxcbanj">
                    Pay from balances
                  </span>
                </div>
                <p className="text-xs text-gray-500 ml-7" data-id="element-3657" data-mp="2v5mifdw6x20d">
                  Use your existing acquiring balance or linked cards.
                </p>
              </button>

              <button onClick={() => setFundingMode('financing')} className={`p-4 rounded-xl border text-left transition-all relative overflow-hidden ${fundingMode === 'financing' ? 'border-[var(--brand-primary)] ring-1 ring-[var(--brand-primary)] bg-white shadow-sm' : 'border-gray-200 bg-gray-50 hover:bg-white'}`} data-id="element-3658" data-mp="2q589zuqofq1t">
                {fundingMode !== 'financing' && <div className="absolute top-0 right-0 bg-[var(--brand-primary)] text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg" data-id="element-3659" data-mp="15pa5o6fn7u8y">
                    RECOMMENDED
                  </div>}
                <div className="flex items-center gap-3 mb-1" data-id="element-3660" data-mp="d14ild149t5y">
                  <TrendingUpIcon size={18} className={fundingMode === 'financing' ? 'text-[var(--brand-primary)]' : 'text-gray-500'} data-id="element-3661" data-mp="cyiejndfpvsh" />
                  <span className={`font-semibold ${fundingMode === 'financing' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-3662" data-mp="rrc4k2qc1ube">
                    Finance these bills
                  </span>
                </div>
                <p className="text-xs text-gray-500 ml-7" data-id="element-3663" data-mp="3ncv0k9ba57u7">
                  Free up cash flow. We pay your suppliers now, you repay over
                  time.
                </p>
              </button>
            </div>

            {fundingMode === 'financing' ? <div className="space-y-4 mb-6" data-id="element-3664" data-mp="3s4d1wyh7tafp">
                <div className="flex items-center justify-between mb-2" data-id="element-3665" data-mp="3s5o4ww2u6e5r">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-3666" data-mp="34e8zgufrjgqa">
                    Select a financing offer
                  </h3>
                  <span className="text-xs text-gray-500" data-id="element-3667" data-mp="30ertlzwfchkl">
                    Amount to finance:{' '}
                    <strong className="text-gray-900" data-id="element-3668" data-mp="3ee9lio50oay6">
                      MYR{' '}
                      {totalAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                    </strong>
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3669" data-mp="on4piu59b2sd">
                  {financingOffers.map(offer => <div key={offer.id} onClick={() => setSelectedOfferId(offer.id)} className={`relative p-5 rounded-xl border cursor-pointer transition-all ${selectedOfferId === offer.id ? 'border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)] bg-white shadow-md' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-3670" data-mp="26nm1un3255j2">
                      {offer.recommended && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap shadow-sm" data-id="element-3671" data-mp="2ucy8pgawx1i7">
                          {offer.highlight.toUpperCase()}
                        </div>}
                      {!offer.recommended && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-100 text-gray-600 border border-gray-200 text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap" data-id="element-3672" data-mp="3dzcqv72q2zse">
                          {offer.highlight.toUpperCase()}
                        </div>}

                      <div className="text-center mb-4 mt-2" data-id="element-3673" data-mp="1gbjzvu4gf93a">
                        <h4 className="font-bold text-gray-900 text-lg" data-id="element-3674" data-mp="1ge6g0i5euwx4">
                          {offer.name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-3675" data-mp="283riv17fmxku">
                          {offer.tagline}
                        </p>
                      </div>

                      <div className="space-y-3 mb-5" data-id="element-3676" data-mp="1zb7nqaqcnb4r">
                        <div className="flex justify-between items-center text-sm" data-id="element-3677" data-mp="1z9mtynbuzw7y">
                          <span className="text-gray-500" data-id="element-3678" data-mp="1ub1votg63mm5">Term</span>
                          <span className="font-semibold text-gray-900" data-id="element-3679" data-mp="2rr8p6s56kk79">
                            {offer.termMonths} months
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-3680" data-mp="2ubwxkjqm1v6t">
                          <span className="text-gray-500" data-id="element-3681" data-mp="2ue9lddj1uzrs">Holdback</span>
                          <span className="font-semibold text-gray-900" data-id="element-3682" data-mp="2zbjhtd4uwbp4">
                            {offer.holdbackRate}% daily
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-3683" data-mp="38chhxkc6bz2f">
                          <span className="text-gray-500" data-id="element-3684" data-mp="3d9redjxzdazr">Total fees</span>
                          <span className="font-semibold text-gray-900" data-id="element-3685" data-mp="3i9cbdotlb99u">
                            MYR{' '}
                            {offer.totalFees.toLocaleString('en-US', {
                      minimumFractionDigits: 0
                    })}
                          </span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-100 text-center" data-id="element-3686" data-mp="3vz7ei65r71fn">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-3687" data-mp="4vioijmueyba">
                          Total repayable
                        </div>
                        <div className="text-xl font-bold text-[var(--brand-primary)]" data-id="element-3688" data-mp="1wwis5h66rc6l">
                          MYR{' '}
                          {offer.totalRepayable.toLocaleString('en-US', {
                    minimumFractionDigits: 0
                  })}
                        </div>
                      </div>

                      {selectedOfferId === offer.id && <div className="absolute top-3 right-3 text-[var(--brand-primary)]" data-id="element-3689" data-mp="2fn0en40fdbji">
                          <CheckIcon size={20} data-id="element-3690" data-mp="2tmt1upcqt9mq" />
                        </div>}
                    </div>)}
                </div>

                {showValidationErrors && !selectedOfferId && <p className="text-xs text-red-500 flex items-center gap-1.5 mt-2" data-id="element-3691" data-mp="z7yvv2ebnv3z">
                    <AlertTriangleIcon size={14} data-id="element-3692" data-mp="ua72s51xy2y7" />
                    Please select a financing offer to continue.
                  </p>}
              </div> : <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6" data-id="element-3693" data-mp="3avu85jgtmdjf">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-3694" data-mp="2x58npqbpxvjf">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-3695" data-mp="315925ayti6qh">
                    {t('pages.BillApprovalWizard.payments_to_be_scheduled')}
                  </h3>
                  {showValidationErrors && invalidCount > 0 && <span className="text-xs text-red-500 font-medium flex items-center gap-1" data-id="element-3696" data-mp="2s3jac410x5s6">
                      <AlertTriangleIcon size={12} data-id="element-3697" data-mp="2e7o0sosil71g" />
                      {invalidCount} {t('pages.BillApprovalWizard.bill')}
                      {invalidCount !== 1 ? 's' : ''}{' '}
                      {t('pages.BillApprovalWizard.missing_payment_method')}
                    </span>}
                </div>
                <div className="overflow-x-auto" data-id="element-3698" data-mp="30c46aowij5xu">
                  <table className="w-full text-left" data-id="element-3699" data-mp="30bkqbdqfarmg">
                    <thead data-id="element-3700" data-mp="2vdarmt5hp8w3">
                      <tr className="border-b border-gray-100" data-id="element-3701" data-mp="2ve2i5pk8cgfi">
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-3702" data-mp="2qm18mbeg53t6">
                          {t('pages.BillApprovalWizard.bill_details')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-3703" data-mp="2hlcp516aab8w">
                          {t('pages.BillApprovalWizard.bill_due_date')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" data-id="element-3704" data-mp="27mwyt0f2z60g">
                          {t('pages.BillApprovalWizard.payment_date')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap text-right" data-id="element-3705" data-mp="5wljrlysfp66">
                          {t('pages.BillApprovalWizard.amount')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" style={{
                    minWidth: 200
                  }} data-id="element-3706" data-mp="9essctxqb3r5">
                          {t('pages.BillApprovalWizard.pay_bill_with')}
                        </th>
                        <th className="px-5 py-3 text-xs font-medium text-gray-500 whitespace-nowrap" style={{
                    minWidth: 200
                  }} data-id="element-3707" data-mp="n78rvkrf8jnb">
                          {t('pages.BillApprovalWizard.designated_approver')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-3708" data-mp="1avi5hzehe13d">
                      {selectedBills.map(bill => {
                  const rowState = billRows.find(r => r.billId === bill.id);
                  const supplierPay = getSupplierPaymentMethod(bill.supplierId);
                  const paymentDate = getPaymentDate(scheduleType, bill.dueDate);
                  const isPastDue = scheduleType === 'due_date' && new Date(bill.dueDate) < new Date();
                  const hasError = showValidationErrors && rowState?.paymentMethod === '';
                  return <tr key={bill.id} className={hasError ? 'bg-red-50/40' : ''} data-id="element-3709" data-mp="2tke8fsr02fhq">
                            <td className="px-5 py-4" data-id="element-3710" data-mp="mupsipn03r1v">
                              <div className="text-sm font-semibold text-gray-900" data-id="element-3711" data-mp="rtavuxxjuquj">
                                {bill.supplierName}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5" data-id="element-3712" data-mp="wxv8hua5okju">
                                {bill.billNumber}
                              </div>
                            </td>
                            <td className="px-5 py-4 text-sm text-gray-900 whitespace-nowrap" data-id="element-3713" data-mp="15yky2g8tpep4">
                              {formatDate(bill.dueDate)}
                            </td>
                            <td className="px-5 py-4 whitespace-nowrap" data-id="element-3714" data-mp="1kpk17bqw3b1b">
                              <div className="flex items-center gap-1.5" data-id="element-3715" data-mp="1fnuqjx7kwzdr">
                                <span className="text-sm font-medium text-gray-900" data-id="element-3716" data-mp="1tof7soxuin55">
                                  {formatDate(paymentDate)}
                                </span>
                                {isPastDue && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700" data-id="element-3717" data-mp="3vfscbcvwit0o">
                                    {t('pages.BillApprovalWizard.adjusted')}
                                  </span>}
                                {scheduleType === 'today' && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700" data-id="element-3718" data-mp="24afdyul6a4wf">
                                    {t('pages.BillApprovalWizard.immediate')}
                                  </span>}
                              </div>
                            </td>
                            <td className="px-5 py-4 text-right whitespace-nowrap" data-id="element-3719" data-mp="2n288wujsy06s">
                              <span className="text-sm font-semibold text-gray-900" data-id="element-3720" data-mp="2s11memz39r6y">
                                {bill.currency}{' '}
                                {bill.amount.toLocaleString('en-US', {
                          minimumFractionDigits: 2
                        })}
                              </span>
                            </td>
                            <td className="px-5 py-4" data-id="element-3721" data-mp="pfp9gg163390">
                              <div className="relative" data-id="element-3722" data-mp="lcj4dbpsji44">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3723" data-mp="lf5d3eof43nx">
                                  {rowState?.paymentMethod?.startsWith('card') ? <CreditCardIcon size={14} className="text-gray-400" data-id="element-3724" data-mp="191tyiacmye9a" /> : <WalletIcon size={14} className="text-gray-400" data-id="element-3725" data-mp="udgyn2afgb9i" />}
                                </div>
                                <select value={rowState?.paymentMethod || ''} onChange={e => updateBillRow(bill.id, {
                          paymentMethod: e.target.value
                        })} className={`w-full appearance-none pl-8 pr-8 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A] bg-white ${hasError ? 'border-red-300 text-red-600' : 'border-gray-200 text-gray-900'}`} data-id="element-3726" data-mp="1n0tn5i05et5g">
                                  {supplierPay.options.length === 0 && <option value="" data-id="element-3727" data-mp="1ljmky6l1my88">
                                      {t('pages.BillApprovalWizard.no_payment_method_configured')}
                                    </option>}
                                  {supplierPay.options.map(opt => <option key={opt.value} value={opt.value} data-id="element-3728" data-mp="2ivjib4bjoch">
                                      {opt.label}
                                    </option>)}
                                </select>
                                <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3729" data-mp="7d81shu26tmn" />
                              </div>
                              {hasError && <p className="text-[10px] text-red-500 mt-1" data-id="element-3730" data-mp="3oqbvinkrq4b9">
                                  {t('pages.BillApprovalWizard.payment_method_required')}
                                </p>}
                            </td>
                            <td className="px-5 py-4" data-id="element-3731" data-mp="3svm06hx4vw91">
                              <div className="relative" data-id="element-3732" data-mp="3sx6ty5bmjb5u">
                                <select value={rowState?.designatedApprover || 'auto'} onChange={e => updateBillRow(bill.id, {
                          designatedApprover: e.target.value
                        })} className="w-full appearance-none px-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A] bg-white" data-id="element-3733" data-mp="1nq8h78xjwp4">
                                  {APPROVERS.map(a => <option key={a.value} value={a.value} data-id="element-3734" data-mp="2qgvgu7qep2qa">
                                      {a.label}
                                    </option>)}
                                </select>
                                <ChevronDownIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3735" data-mp="2nuutwra2zu8a" />
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100" data-id="element-3736" data-mp="20634u8rsfrzq">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-3737" data-mp="1v78pjgt14lkd">
                    {t('pages.BillApprovalWizard.after_confirming_the_payment_method_and')}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-3738" data-mp="1m73g7149zd3k">
                    {t('pages.BillApprovalWizard.your_selected_payment_schedule_is')}{' '}
                    <span className="font-semibold text-gray-700" data-id="element-3739" data-mp="353x57lw7rdi2">
                      {scheduleType === 'due_date' ? 'On the billing due date' : scheduleType === 'today' ? 'Immediately (today)' : 'On the next cycle'}
                    </span>
                  </p>
                </div>
              </div>}
            {/* Validation error */}
            {showValidationErrors && !allValid && fundingMode === 'standard' && <div className="text-center mb-6" data-id="element-3740" data-mp="2r6gsjsnbqnhv">
                  <p className="text-xs text-red-500 flex items-center justify-center gap-1.5" data-id="element-3741" data-mp="2ch2jymrkoa3j">
                    <AlertTriangleIcon size={14} data-id="element-3742" data-mp="2hfm88ttn1xz2" />
                    {t('pages.BillApprovalWizard.unable_to_schedule_payments_please_ensure')}
                  </p>
                </div>}
            <div className="flex justify-center gap-4" data-id="element-3743" data-mp="28fz4i1dfsg2u">
              <button onClick={() => setStep(1)} className="px-8 py-2.5 text-sm font-medium text-[#9F140F] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3744" data-mp="1ot5qug5r1k9e">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={handleConfirmSchedule} className={`px-8 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm ${allValid ? 'bg-[#E2231A] hover:bg-[#C41B14] text-white' : 'bg-[#E2231A]/80 text-white hover:bg-[#C41B14]'}`} data-id="element-3745" data-mp="1tq5wwr2lmn0i">
                {t('pages.BillApprovalWizard.confirm_and_schedule')}
              </button>
            </div>
          </div>}

        {/* ========== STEP 3: Success ========== */}
        {step === 3 && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-2xl mx-auto text-center" data-id="element-3746" data-mp="2qfncbxrdvnfe">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3747" data-mp="1jrkya2ce4jop">
              <CheckCircleIcon size={32} className="text-green-600" data-id="element-3748" data-mp="1esqht71wb77r" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-3749" data-mp="1sjvafp6uiz8l">
              {t('pages.BillApprovalWizard.payments_scheduled_successfully')}
            </h1>
            <p className="text-sm text-gray-500 mb-10 max-w-md mx-auto" data-id="element-3750" data-mp="1nrvee7twcqha">
              {selectedBills.length} {t('pages.BillApprovalWizard.bill')}
              {selectedBills.length !== 1 ? 's have' : ' has'}{' '}
              {t('pages.BillApprovalWizard.been_submitted_for_approval_approvers_will')}
            </p>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-id="element-3751" data-mp="27hnmu8h3j7ik">
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-3752" data-mp="h3ya74tk4sw2">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3753" data-mp="d3mn7ldq2ghf">
                  {t('pages.BillApprovalWizard.total_scheduled')}
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-3754" data-mp="31anc8bf1ou9">
                  {t('pages.BillApprovalWizard.usd')}{' '}
                  {totalAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2
              })}
                </span>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-3755" data-mp="3uc0vkzfiip2b">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3756" data-mp="3ff9w6q5u9dcp">
                  {t('pages.BillApprovalWizard.assigned_to_you')}
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-3757" data-mp="36gg6zkuu7p96">
                  {assignedToMe.length} {t('pages.BillApprovalWizard.bill')}
                  {assignedToMe.length !== 1 ? 's' : ''}
                </span>
                {assignedToMe.length > 0 && <p className="text-[10px] text-[#9F140F] mt-1 font-medium" data-id="element-3758" data-mp="19nmnmv3jwr4y">
                    {t('pages.BillApprovalWizard.view_under_assigned_to_me_tab')}
                  </p>}
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5 text-left" data-id="element-3759" data-mp="135taybl2bfj3">
                <span className="text-xs text-gray-500 block mb-1" data-id="element-3760" data-mp="y92uvo99cjxo">
                  {t('pages.BillApprovalWizard.autoassigned')}
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-3761" data-mp="1qwp9drk0x8zb">
                  {autoAssigned.length} {t('pages.BillApprovalWizard.bill')}
                  {autoAssigned.length !== 1 ? 's' : ''}
                </span>
                <p className="text-[10px] text-gray-400 mt-1" data-id="element-3762" data-mp="1h6drt27f319y">
                  {t('pages.BillApprovalWizard.based_on_threshold_rules')}
                </p>
              </div>
            </div>

            {/* Scheduled bills detail */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-10 text-left" data-id="element-3763" data-mp="29orjek25tr0l">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center" data-id="element-3764" data-mp="24ux1nm647m45">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-3765" data-mp="2h6zbvs6yy900">
                  {t('pages.BillApprovalWizard.scheduled_payments')}
                </h3>
                {fundingMode === 'financing' && selectedOfferId && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[var(--brand-primary-light)] text-[var(--brand-primary-dark)] text-xs font-semibold" data-id="element-3766" data-mp="2r66ryn538hqt">
                    <TrendingUpIcon size={14} data-id="element-3767" data-mp="305boxp10430v" />
                    Financed via{' '}
                    {financingOffers.find(o => o.id === selectedOfferId)?.name}
                  </span>}
              </div>
              <div className="divide-y divide-gray-100" data-id="element-3768" data-mp="3ev9jcmpixoyi">
                {selectedBills.map(bill => {
              const rowState = billRows.find(r => r.billId === bill.id);
              const paymentDate = getPaymentDate(scheduleType, bill.dueDate);
              const approverLabel = rowState?.designatedApprover === 'auto' ? 'Auto-assigned' : rowState?.designatedApprover || 'Auto-assigned';
              return <div key={bill.id} className="px-6 py-4 flex items-center justify-between" data-id="element-3769" data-mp="p60fmv1vmuyg">
                      <div className="flex items-center gap-4 min-w-0" data-id="element-3770" data-mp="1iskh5y5x2a5">
                        <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3771" data-mp="3sotowvnmgxmt">
                          <CheckCircleIcon size={16} className="text-green-500" data-id="element-3772" data-mp="afjezdx2g92m" />
                        </div>
                        <div className="min-w-0" data-id="element-3773" data-mp="1lyvv33burshm">
                          <div className="text-sm font-medium text-gray-900 truncate" data-id="element-3774" data-mp="1gyhir937xwds">
                            {bill.supplierName}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-3775" data-mp="1tgbup8eg8qug">
                            {bill.billNumber}{' '}
                            {t('pages.BillApprovalWizard.payment_on')}{' '}
                            {formatDate(paymentDate)}{' '}
                            {t('pages.BillApprovalWizard.approver')}{' '}
                            {approverLabel}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 flex-shrink-0 ml-4" data-id="element-3776" data-mp="2d3rph82xbjoh">
                        {bill.currency}{' '}
                        {bill.amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </div>
                    </div>;
            })}
              </div>
            </div>

            <div className="flex justify-center gap-4" data-id="element-3777" data-mp="2vw3nxjqgi0tc">
              <button onClick={() => navigate('/bills')} className="px-8 py-2.5 text-sm font-medium text-white bg-[#E2231A] rounded-lg hover:bg-[#C41B14] transition-colors shadow-sm" data-id="element-3778" data-mp="35r15u0jj5ilq">
                {t('pages.BillApprovalWizard.back_to_bills')}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}